import { Spinner } from '@primer/react';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { Flash } from '@upright/ui/common/generic/components/primer-custom/flash';
import { CheckStreamlineCoreLine } from '@upright/ui/icons';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalculatingResultsErrorBanner } from './calculating-results-error-banner';
import { useDmaV2MaterialityResultsBasePath } from './utils';
import { Box, Stack } from '@upright/ui/common/generic/components/layout';

export interface CalculatingResultsBannerProps {
  children: ReactNode;
  jobStatus: JobStatus | undefined;
  jobId?: number;
  refetchJobStatus: () => void;
  gap?: 'lg' | '2xl';
}

export const FREEMIUM_DMA_RESULT_BANNER_ID = 'freemium-dma-results-banner';

export function WithCalculatingResultsBanner(props: CalculatingResultsBannerProps) {
  const { children, jobStatus, jobId, refetchJobStatus, gap = '2xl' } = props;
  return (
    <Stack inlineSize="100%">
      <Box paddingBlockEnd={gap}>
        <CalculatingResultsBanner status={jobStatus} jobId={jobId} refetchJobStatus={refetchJobStatus} gap={gap} />
      </Box>
      {children}
    </Stack>
  );
}

interface Props {
  status: JobStatus | undefined;
  jobId?: number;
  refetchJobStatus: () => void;
  gap?: 'lg' | '2xl';
}

export function CalculatingResultsBanner(props: Props) {
  const resultsPath = useDmaV2MaterialityResultsBasePath();

  if (!props.status) {
    return null;
  }

  switch (props.status) {
    case JobStatus.RUNNING:
    case JobStatus.QUEUED:
    case JobStatus.IDLE: // should not really happen
    case JobStatus.DELAYED: // should not really happen
      return (
        <Box marginBlockStart={props.gap} asChild>
          <Flash icon={<Spinner size="small" />} variant="default" dismissable={false}>
            <b>Calculating updated results.</b> This usually takes a few minutes.
          </Flash>
        </Box>
      );
    case JobStatus.SUCCESS:
      return (
        <Box marginBlockStart={props.gap} asChild>
          <Flash
            dismissable={true}
            title="Results ready"
            icon={<CheckStreamlineCoreLine />}
            variant="success"
            timeout={20 * 1000}
            flashIdForDismissalTracking={FREEMIUM_DMA_RESULT_BANNER_ID}
          >
            <b>Your results have been updated.</b> <Link to={resultsPath}>View results.</Link>
          </Flash>
        </Box>
      );
    case JobStatus.ERROR:
      return (
        <Box marginBlockStart={props.gap} asChild>
          <CalculatingResultsErrorBanner jobId={props.jobId} refetchJobStatus={props.refetchJobStatus} />
        </Box>
      );
  }
}
